import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  Session,
  AuthError,
  AuthChangeEvent,
  Subscription,
  RealtimeChannel,
} from "@supabase/supabase-js";
import { supabase } from "../main"; // Assuming supabase client is initialized here and exported

// =============================================================================
// Authentication Flow (Technical Overview)
// =============================================================================
//
// This Zustand store manages user authentication state using Supabase Auth.
// Supabase provides backend services including authentication, database, etc.
//
// Core Concepts:
// ----------------
// 1. Supabase Client: An object (`supabase`) used to interact with Supabase services.
//    It's initialized once (ideally in `src/main.ts` or `src/lib/supabaseClient.ts`)
//    with your project URL and anon key.
// 2. Supabase Auth: Handles user sign-up, login, logout, password resets, etc.
//    It uses JSON Web Tokens (JWTs) to manage user sessions securely.
// 3. onAuthStateChange Listener: This is the *central piece* of our integration.
//    Instead of manually checking the user's status repeatedly, we subscribe
//    to Supabase Auth events. Supabase tells *us* whenever the user logs in,
//    logs out, their session is refreshed, or the initial state is checked.
// 4. Zustand State: We store the user's authentication status (session info,
//    user profile, whether they are logged in) in this Zustand store. Components
//    can subscribe to this store to reactively update the UI.
// 5. LocalStorage Persistence: We persist some auth state (like the user object
//    and session) to localStorage. This helps maintain the session across page
//    refreshes, but the `onAuthStateChange` listener is the primary source of
//    truth when the app loads.
//
// Flow Overview:
// ----------------
// 1. App Initialization (`App.tsx`):
//    - When the app starts, a `useEffect` hook calls `initializeAuthListener()` from this store.
// 2. Listener Initialization (`initializeAuthListener`):
//    - This action subscribes to Supabase's `onAuthStateChange`.
//    - The listener immediately checks the *initial* auth state (e.g., user might
//      already be logged in from a previous visit).
// 3. Event Handling (Listener Callback):
//    - Whenever an auth event happens (login, logout, token refresh):
//      a. The callback function inside `onAuthStateChange` runs.
//      b. It receives the `event` type (e.g., 'SIGNED_IN', 'SIGNED_OUT') and the current `session`.
//      c. Based on the event and session, it updates the Zustand state (`user`, `session`, `isAuthenticated`, etc.).
//      d. If the user logs in ('SIGNED_IN', 'INITIAL_SESSION'), it fetches their associated profile data
//         (like credits) from the `profiles` table in the Supabase database.
//      e. Also, upon login/session restoration ('SIGNED_IN', 'INITIAL_SESSION'), it sets up a **Supabase Realtime subscription**
//         listening for changes (INSERT, UPDATE, DELETE) specifically on the user's row in the `profiles` table.
//         When a change occurs (e.g., credits updated), the subscription receives the new data and updates the `user` object
//         in the Zustand state, ensuring the credit display is always current.
//      f. Updates `localStorage` with the auth token for potential use by API clients.
// 4. Auth Actions (`login`, `signup`, `logout`, `signInWithGoogle`):
//    - These actions in the store are called by UI components (e.g., Login Page).
//    - They primarily just call the corresponding Supabase Auth function (e.g., `supabase.auth.signInWithPassword`).
//    - They *do not* directly set the state upon success. They rely on the `onAuthStateChange` listener
//      (Step 3) to detect the successful authentication and update the state accordingly.
//    - They handle immediate errors (e.g., network error, invalid credentials during the call itself)
//      and update the loading state.
// 5. State Consumption (React Components):
//    - Components use `useAuthStore()` to access the authentication state (`isAuthenticated`, `user`, `isLoading`).
//    - The UI updates automatically whenever the state changes in the store (due to the listener).
// 6. App Unmount (`App.tsx`):
//    - The `useEffect` hook returns a cleanup function that unsubscribes from the `onAuthStateChange` listener
//      and the profile change listener (if active) to prevent memory leaks.
//
// =============================================================================

// =============================================================================
// Authentication Flow (User Perspective)
// =============================================================================
//
// This section describes the common authentication journeys a user might take:
//
// 1. Anonymous Access:
//    - User visits the site (e.g., Landing Page).
//    - User clicks a "Try it out" or similar button.
//    - Behind the scenes, `signInAnonymously()` is called.
//    - The user gets a temporary, anonymous session (`is_anonymous: true`).
//    - They can use core features, potentially with limitations (e.g., free credits).
//
// 2. Converting Anonymous User to Permanent (Linking):
//    - While logged in anonymously, the user decides to create a permanent account
//      (e.g., by clicking "Sign in/Sign up" on the Login Page, or trying to purchase credits).
//    - Option A: Google:
//        - User clicks "Sign in with Google".
//        - `GoogleButton` detects the anonymous status and calls `linkWithGoogle()`.
//        - User is redirected to Google to authorize.
//        - Upon return, the Supabase listener detects the updated user (`USER_UPDATED` event),
//          the `is_anonymous` flag becomes `false`, and the account is now permanent.
//        - Conflict: If the Google account is already linked to *another* user, the initial
//          `linkWithGoogle` fails (409). `GoogleButton` catches this and automatically calls
//          `signInWithGoogle()` instead, logging the user into the existing account.
//          (Data from the anonymous session is not merged).
//    - Option B: Phone:
//        - User enters their phone number in a profile/settings/login form.
//        - The app calls `updatePhoneNumber()` (this function handles linking for anonymous users).
//        - User receives an SMS OTP.
//        - User enters the OTP.
//        - The app calls `verifyPhoneUpdateOtp()` (using type `phone_change`).
//        - The listener detects `USER_UPDATED`, `is_anonymous` becomes `false`.
//        - Conflict: If the phone number is already linked, the operation fails.
//          The store throws `auth.errors.identityAlreadyExists`, which the UI should handle
//          (e.g., prompt the user to log in with that phone number instead).
//    - Option C: Email:
//        - User enters their email address.
//        - App calls `linkWithEmail()`.
//        - User receives an email with an OTP or magic link.
//        - User enters OTP or clicks link.
//        - App calls `verifyEmailLinkOtp()` (using type `email_change`).
//        - Listener detects `USER_UPDATED`, `is_anonymous` becomes `false`.
//        - Optional: User might be prompted to set a password via `setLinkedPassword()`.
//        - Conflict: Similar to phone, if the email is already linked, the store throws
//          `auth.errors.identityAlreadyExists` for the UI to handle.
//
// 3. Direct Sign Up / Sign In (Non-Anonymous):
//    - User is not logged in (or logged out).
//    - Option A: Google:
//        - User clicks "Sign in/Sign up with Google".
//        - `GoogleButton` calls `signInWithGoogle()`.
//        - Redirect flow via Google.
//        - Listener detects `SIGNED_IN`/`INITIAL_SESSION`. User is logged in.
//    - Option B: Phone:
//        - User enters phone number on Login Page.
//        - App calls `signInWithPhone()`.
//        - User enters received OTP.
//        - App calls `verifyPhoneOtp()` (using type `sms`).
//        - Listener detects `SIGNED_IN`. User is logged in.
//    - Option C: Email/Password (if implemented in UI):
//        - User enters email/password on Login Page.
//        - App calls `login()`.
//        - Listener detects `SIGNED_IN`.
//        - OR User enters email/password on Sign Up Page.
//        - App calls `signup()`.
//        - User may need to confirm email.
//        - Listener detects `SIGNED_IN` after confirmation (if required).
//
// 4. Forced Login:
//    - User (potentially anonymous) tries to access a restricted area (e.g., Purchase Page).
//    - The page component checks `user.is_anonymous` or `!isAuthenticated`.
//    - User is redirected to the Login Page, possibly with a message indicating why.
//
// 5. Logout:
//    - User clicks a "Logout" button.
//    - App calls `logout()`.
//    - Listener detects `SIGNED_OUT`. User session is cleared.
//
// Key Principle: Most state updates related to authentication status (`user`, `session`,
// `isAuthenticated`, `is_anonymous`) are driven reactively by the `onAuthStateChange`
// listener, not directly by the `login`, `signup`, `link`, or `logout` action calls themselves.
// The actions initiate the process with Supabase; the listener reacts to the outcome.
// =============================================================================

// Define types for our store
interface UserProfile {
  free_credits: number;
  paid_credits: number;
  last_free_credit_refresh?: string;
}

// Combined User type includes profile data and Supabase auth user info
interface User extends UserProfile {
  id: string; // Supabase User ID
  email: string;
  phone: string;
  is_anonymous: boolean;
}

// Define the structure of the authentication state managed by Zustand
interface AuthState {
  user: User | null; // Holds the logged-in user's data (including profile), or null if logged out
  session: Session | null; // Supabase session object (contains tokens, user ID, etc.), or null
  token: string | null; // The access token (JWT) for the current session
  isLoading: boolean; // True while checking initial auth state or during login/logout actions
  isAuthenticated: boolean; // Convenience flag: true if session and user exist, false otherwise
  authSubscription: Subscription | null; // Holds the subscription object for the listener, used for unsubscribing
  profileSubscriptionChannel: RealtimeChannel | null; // Holds the realtime channel for profile updates
  phoneNumberForVerification: string | null; // Stores the phone number awaiting OTP verification
  emailForVerification: string | null; // Added for email linking
  isOtpSent: boolean; // True if the OTP has been successfully sent to the user
  previousAnonymousUserId: string | null; // Added to track anon ID before potential merge/login

  // Actions callable by components or effects
  login: (email: string, password: string) => Promise<void>; // Initiates email/password login
  signup: (email: string, password: string) => Promise<void>; // Initiates email/password signup
  logout: () => Promise<void>; // Initiates logout
  signInWithGoogle: () => Promise<void>; // Initiates Google OAuth login
  signInWithPhone: (phoneNumber: string) => Promise<void>; // Initiates phone OTP login
  verifyPhoneOtp: (token: string) => Promise<void>; // Verifies the phone OTP for login
  updatePhoneNumber: (newPhoneNumber: string) => Promise<void>; // Initiates phone number update
  verifyPhoneUpdateOtp: (token: string) => Promise<void>; // Verifies OTP for phone number update
  signInAnonymously: () => Promise<void>; // Initiates anonymous sign-in
  fetchUserProfile: (userId: string) => Promise<Partial<UserProfile>>; // Fetches profile data from DB
  initializeAuthListener: () => () => void; // Sets up the Supabase auth listener, returns unsubscribe function

  // --- Anonymous User Conversion Actions ---
  linkWithGoogle: () => Promise<void>; // Links Google account to an existing anonymous user
  linkWithEmail: (email: string) => Promise<void>; // Sends verification to link email to an anonymous user
  verifyEmailLinkOtp: (token: string) => Promise<void>; // Verifies OTP/link to link email and promote anon user
  setLinkedPassword: (password: string) => Promise<void>; // Sets a password for a newly linked/promoted account
}

// Define a type for the expected payload from profile changes
interface ProfilePayload {
  free_credits?: number;
  paid_credits?: number;
  last_free_credit_refresh?: string;
  user_id?: string; // Include user_id for completeness
}

/**
 * Helper function to map common Supabase Auth errors to translation keys
 * for user-friendly error messages in the UI.
 */
const mapAuthErrorToKey = (error: AuthError): string => {
  console.error("Supabase Auth Error:", error); // Log original error for debugging
  // Specific error message checks
  if (error.message.includes("Invalid login credentials")) {
    return "auth.errors.invalidCredentials";
  }
  if (error.message.includes("Email not confirmed")) {
    return "auth.errors.emailNotConfirmed";
  }
  if (error.message.includes("User already registered")) {
    return "auth.errors.userAlreadyRegistered";
  }
  if (
    error.message.includes("identity already exists") || // Check message
    error.status === 409 // Check status code for conflicts
  ) {
    return "auth.errors.identityAlreadyExists"; // New specific key
  }
  if (error.message.includes("NetworkError")) {
    return "auth.errors.networkError";
  }
  // Fallback for unknown errors
  return "auth.errors.unknown";
};

// Create the Zustand store for authentication state.
// `persist` middleware automatically saves/loads parts of the state to/from localStorage.
export const useAuthStore = create<AuthState>()(
  persist(
    // `set` is used to update the store's state.
    // `get` is used to read the current state within actions.
    (set, get) => ({
      // --- Initial State ---
      user: null,
      session: null,
      token: null,
      isLoading: true, // Start loading as true. The listener will set this to false once the initial state is known.
      isAuthenticated: false,
      authSubscription: null, // No active listener subscription initially.
      profileSubscriptionChannel: null, // Initialize profile subscription channel
      phoneNumberForVerification: null, // Initialize phone number state
      emailForVerification: null, // Initialize new state
      isOtpSent: false, // Initialize OTP sent state
      previousAnonymousUserId: null, // Initialize new state

      // --- Actions ---

      /**
       * Fetches additional user profile data from the 'profiles' table in the Supabase database.
       * This is called by the auth listener when a user signs in.
       * @param userId The ID of the user whose profile needs fetching.
       * @returns A partial UserProfile object or default values.
       */
      fetchUserProfile: async (
        userId: string
      ): Promise<Partial<UserProfile>> => {
        if (!userId) {
          console.warn("fetchUserProfile called with invalid userId");
          return { free_credits: 0, paid_credits: 0 };
        }
        console.log(
          `[fetchUserProfile] Attempting to fetch profile for userId: ${userId}`
        );
        try {
          // Use the Supabase client to query the 'profiles' table
          console.log(
            "[fetchUserProfile] Before supabase.from('profiles').select..."
          );
          const { data: profileData, error: profileError } = await supabase
            .from("profiles") // Specify the table name
            .select("free_credits, paid_credits, last_free_credit_refresh") // Select specific columns
            .eq("user_id", userId) // Filter by the user's ID (ensure RLS policy allows this)
            .maybeSingle(); // Expect 0 or 1 result, doesn't error if 0 found
          console.log(
            "[fetchUserProfile] After supabase.from('profiles').select...",
            { profileData, profileError }
          );

          // Handle potential errors during the database query
          if (profileError) {
            console.error(
              "[fetchUserProfile] Error during Supabase query:",
              profileError
            );
            // Return default values on error to avoid breaking the auth flow
            return { free_credits: 0, paid_credits: 0 };
          }

          // Return the fetched profile data, providing defaults if columns are null/undefined
          console.log("[fetchUserProfile] Successfully fetched profile data.");
          return {
            free_credits: profileData?.free_credits ?? 0,
            paid_credits: profileData?.paid_credits ?? 0,
            last_free_credit_refresh: profileData?.last_free_credit_refresh,
          };
        } catch (error) {
          // Catch any unexpected errors during the fetch process
          console.error(
            "[fetchUserProfile] Unexpected error in catch block:",
            error
          );
          return { free_credits: 0, paid_credits: 0 }; // Return default on unexpected error
        }
      },

      /**
       * Initializes the Supabase authentication state change listener.
       * This is the core function that keeps the app's auth state in sync with Supabase.
       * It should be called once when the application loads (e.g., in App.tsx useEffect).
       * @returns An unsubscribe function to clean up the listener on component unmount.
       */
      initializeAuthListener: () => {
        console.log("Initializing Supabase auth listener...");

        // --- CRITICAL: Subscribe to Supabase auth events ---
        const { data: listenerData } = supabase.auth.onAuthStateChange(
          // This async callback function runs whenever an auth event occurs
          async (event: AuthChangeEvent, session: Session | null) => {
            console.log("Auth event:", event, session); // Log the event for debugging

            // --- Capture anonymous ID *before* potential state change --- //
            const currentStateBeforeUpdate = get(); // Get state before any changes
            let potentialAnonymousUserId: string | null = null;
            if (currentStateBeforeUpdate.user?.is_anonymous) {
              potentialAnonymousUserId = currentStateBeforeUpdate.user.id;
              console.log(
                `[AUTH_LISTENER] Captured potential anonymous user ID: ${potentialAnonymousUserId}`
              );
              // Store it temporarily in state for reliability across async operations
              // set({ previousAnonymousUserId: potentialAnonymousUserId });
              // Using a local variable within the callback scope is simpler if possible
            }

            // Indicate loading during state transitions, except for silent token refreshes
            if (event !== "TOKEN_REFRESHED") {
              set({ isLoading: true });
            }

            console.log("[AUTH_LISTENER] Entering try block...");
            try {
              // --- Handle different authentication events ---

              // Case 1: User is logged in or session is refreshed/updated
              if (session) {
                // Check for events that indicate an active, valid session
                // These events all mean the user *is* authenticated.
                if (
                  event === "SIGNED_IN" ||
                  event === "INITIAL_SESSION" ||
                  event === "USER_UPDATED" ||
                  event === "TOKEN_REFRESHED" ||
                  event === "MFA_CHALLENGE_VERIFIED" // Added for completeness if MFA is used
                ) {
                  const supabaseUser = session.user; // Get user details from the session
                  const currentState = get(); // Get current state *once*
                  let userProfile: Partial<UserProfile> = {};

                  // --- Fetch profile data ONLY when necessary ---
                  // Fetch if:
                  // 1. No user currently exists in the state (!currentState.user), meaning this is the initial load/login.
                  // 2. The user's data was explicitly updated (USER_UPDATED).
                  const shouldFetchProfile =
                    supabaseUser &&
                    (!currentState.user || event === "USER_UPDATED");

                  if (shouldFetchProfile) {
                    console.log(
                      `Fetching profile for user ${supabaseUser.id} because ${
                        !currentState.user
                          ? "user not in state"
                          : "event is USER_UPDATED"
                      }`
                    );
                    // Call the helper function to get profile data
                    // Use currentState.fetchUserProfile as get() might be stale if called again
                    userProfile = await currentState.fetchUserProfile(
                      supabaseUser.id
                    );
                  } else if (currentState.user) {
                    // For other events (like TOKEN_REFRESHED, SIGNED_IN on focus), reuse existing profile data
                    console.log(
                      `Reusing existing profile data on event ${event} for user ${currentState.user.id}`
                    );
                    userProfile = {
                      free_credits: currentState.user.free_credits,
                      paid_credits: currentState.user.paid_credits,
                      last_free_credit_refresh:
                        currentState.user.last_free_credit_refresh,
                    };
                  } else {
                    // Defensive case: Session exists, not fetching, but no current user to reuse from. Should be rare.
                    console.warn(
                      `[AUTH_LISTENER] Session exists for event ${event}, but no user data to reuse or fetch.`
                    );
                    userProfile = { free_credits: 0, paid_credits: 0 }; // Provide defaults
                  }

                  // Combine Supabase user data and profile data into a single User object
                  const fullUser: User = {
                    id: supabaseUser.id,
                    email: supabaseUser.email || "", // Handle potentially null email
                    // Use fetched or reused profile data, falling back to defaults
                    free_credits: userProfile.free_credits ?? 0,
                    paid_credits: userProfile.paid_credits ?? 0,
                    last_free_credit_refresh:
                      userProfile.last_free_credit_refresh,
                    phone: supabaseUser.phone || "",
                    is_anonymous: supabaseUser.is_anonymous || false,
                  };

                  // --- CRITICAL: Update Zustand state for logged-in user ---
                  set({
                    user: fullUser,
                    session: session,
                    token: session.access_token, // Store the latest access token (JWT)
                    isAuthenticated: true, // User is now considered authenticated
                    isLoading: false, // Finished loading/processing this state
                  });
                  // Store the token in localStorage (e.g., for API calls outside of Supabase client)
                  localStorage.setItem("authToken", session.access_token);

                  // Track successful sign-in events
                  if (event === "SIGNED_IN" || event === "INITIAL_SESSION") {
                    window.trackEvent("auth_signed_in", {
                      method:
                        event === "INITIAL_SESSION"
                          ? "session_restore"
                          : "login",
                    });
                  }

                  // --- Setup or Reuse Profile Realtime Subscription ---
                  if (
                    !currentState.profileSubscriptionChannel || // No channel exists
                    currentState.profileSubscriptionChannel?.topic !==
                      `realtime:public:profiles:user_id=eq.${supabaseUser.id}` // Channel is for a different user (edge case)
                  ) {
                    // Unsubscribe from any old channel first
                    currentState.profileSubscriptionChannel
                      ?.unsubscribe()
                      .catch((error) =>
                        console.error(
                          "Error unsubscribing from old profile channel:",
                          error
                        )
                      );

                    console.log(
                      `[AUTH_LISTENER] Setting up profile subscription for user ${supabaseUser.id}`
                    );
                    const channel = supabase
                      .channel(
                        `public:profiles:user_id=eq.${supabaseUser.id}` // Unique channel name per user
                      )
                      .on(
                        "postgres_changes",
                        {
                          event: "*", // Listen for INSERT, UPDATE, DELETE
                          schema: "public",
                          table: "profiles",
                          filter: `user_id=eq.${supabaseUser.id}`, // Ensure we only get changes for this user
                        },
                        (payload) => {
                          console.log(
                            "[Profile Subscription] Change received:",
                            payload
                          );
                          // Assert the type of the payload data
                          const newProfileData = payload.new as ProfilePayload;
                          // const _oldProfileData = payload.old as ProfilePayload; // Also type the old data if needed

                          // Handle profile updates (INSERT or UPDATE)
                          if (
                            newProfileData &&
                            (newProfileData.free_credits !== undefined ||
                              newProfileData.paid_credits !== undefined ||
                              newProfileData.last_free_credit_refresh !==
                                undefined)
                          ) {
                            set((state) => {
                              // Double-check the user context hasn't changed
                              if (
                                state.user &&
                                state.user.id === supabaseUser.id
                              ) {
                                console.log(
                                  "[Profile Subscription] Updating user credits in state."
                                );
                                return {
                                  user: {
                                    ...state.user,
                                    free_credits:
                                      newProfileData.free_credits ??
                                      state.user.free_credits,
                                    paid_credits:
                                      newProfileData.paid_credits ??
                                      state.user.paid_credits,
                                    last_free_credit_refresh:
                                      newProfileData.last_free_credit_refresh ??
                                      state.user.last_free_credit_refresh,
                                  },
                                };
                              }
                              console.warn(
                                "[Profile Subscription] Received update but user context changed or missing. State:",
                                state.user?.id,
                                "Payload user:",
                                newProfileData.user_id // Access potentially available user_id
                              );
                              return {}; // No change if user doesn't match
                            });
                          } else if (payload.eventType === "DELETE") {
                            // Handle profile deletion if necessary (e.g., log warning)
                            console.warn(
                              `[Profile Subscription] Profile deleted for user ${supabaseUser.id}`
                            );
                            // Optional: Reset credits in state if profile is deleted?
                            // set((state) => ({ user: state.user ? { ...state.user, free_credits: 0, paid_credits: 0 } : null }))
                          }
                        }
                      )
                      .subscribe((status, err) => {
                        if (status === "SUBSCRIBED") {
                          console.log(
                            `[Profile Subscription] Successfully subscribed for user ${supabaseUser.id}`
                          );
                        }
                        if (status === "CHANNEL_ERROR" || err) {
                          console.error(
                            "[Profile Subscription] Subscription error:",
                            err || status
                          );
                          // Attempt to remove the potentially faulty channel from state
                          set((state) =>
                            state.profileSubscriptionChannel === channel
                              ? { profileSubscriptionChannel: null }
                              : {}
                          );
                        }
                        if (status === "CLOSED") {
                          console.log(
                            `[Profile Subscription] Subscription closed for user ${supabaseUser.id}`
                          );
                          // Remove channel from state if it closes unexpectedly
                          set((state) =>
                            state.profileSubscriptionChannel === channel
                              ? { profileSubscriptionChannel: null }
                              : {}
                          );
                        }
                      });

                    // Store the new channel in state
                    set({ profileSubscriptionChannel: channel });
                  } else {
                    console.log(
                      `[AUTH_LISTENER] Reusing existing profile subscription for user ${supabaseUser.id}`
                    );
                  }

                  // --- Check for Anonymous User Conversion with ID Change --- //
                  if (
                    potentialAnonymousUserId && // Was the previous user anonymous?
                    potentialAnonymousUserId !== supabaseUser.id && // Did the ID change?
                    !supabaseUser.is_anonymous // Is the new user NOT anonymous?
                  ) {
                    console.log(
                      `[AUTH_LISTENER] Anonymous user conversion detected with ID change. Previous ID: ${potentialAnonymousUserId}, New ID: ${supabaseUser.id}. Triggering data transfer.`
                    );
                    // Trigger the data transfer
                    // Use the internal helper function
                    await transferAnonymousUserData(
                      potentialAnonymousUserId,
                      supabaseUser.id
                    );
                  } else {
                    console.log(
                      `[AUTH_LISTENER] No anonymous user ID change detected. Prev: ${potentialAnonymousUserId}, New: ${session?.user?.id}, New Anon: ${session?.user?.is_anonymous}`
                    );
                  }
                } else {
                  console.log(
                    `[AUTH_LISTENER] Auth event ${event} with session, but not explicitly handled for state update. Session data:`,
                    session
                  );
                  // If it's an event with a session but doesn't match the above,
                  // we are likely still authenticated. Ensure loading is false.
                  // We don't necessarily need to update user/session if nothing changed.
                  set({ isLoading: false, isAuthenticated: true });
                }

                // Case 2: User is logged out
              } else if (event === "SIGNED_OUT") {
                // --- CRITICAL: Update Zustand state for logged-out user ---
                // Unsubscribe from profile updates channel
                get()
                  .profileSubscriptionChannel?.unsubscribe()
                  .catch((error) =>
                    console.error(
                      "Error unsubscribing from profile channel on logout:",
                      error
                    )
                  );

                set({
                  user: null,
                  session: null,
                  token: null,
                  isAuthenticated: false, // User is no longer authenticated
                  isLoading: false,
                  profileSubscriptionChannel: null, // Clear the channel reference
                });
                // Remove the token from localStorage
                localStorage.removeItem("authToken");
                // Track sign-out event
                window.trackEvent("auth_signed_out");
                console.log(
                  `[AUTH_LISTENER] SIGNED_OUT event. Potential anon ID was: ${potentialAnonymousUserId}`
                );

                // Case 3: Other events or initial state without a session
              } else {
                console.log("Auth state change with no session:", event);
                // If there's no session and it's not a SIGNED_OUT event,
                // treat the user as logged out.
                set({
                  user: null,
                  session: null,
                  token: null,
                  isAuthenticated: false,
                  isLoading: false, // Finished checking, user is not logged in
                });
                localStorage.removeItem("authToken");
              }

              // Catch any errors within the listener callback
            } catch (error) {
              console.error("[AUTH_LISTENER] Caught error in callback:", error);
              // Reset to logged-out state on error
              get()
                .profileSubscriptionChannel?.unsubscribe()
                .catch((err) =>
                  console.error(
                    "Error unsubscribing from profile channel during auth listener error:",
                    err
                  )
                );
              set({
                user: null,
                session: null,
                token: null,
                isAuthenticated: false,
                isLoading: false,
                profileSubscriptionChannel: null, // Clear channel on error
              });
              localStorage.removeItem("authToken");
            } finally {
              console.log("[AUTH_LISTENER] Entering finally block...");
              // --- Ensure loading is set to false after initial check ---
              // This is important so the app doesn't show a loading state indefinitely
              // if the initial check completes quickly without a session.
              if (get().isLoading && event === "INITIAL_SESSION") {
                console.log(
                  "[AUTH_LISTENER] Setting isLoading to false after INITIAL_SESSION."
                );
                set({ isLoading: false });
              }
            }
          } // End of onAuthStateChange callback
        ); // End of supabase.auth.onAuthStateChange call

        // Store the subscription object itself so we can unsubscribe later
        set({ authSubscription: listenerData.subscription });

        // --- Return the unsubscribe function for cleanup ---
        const unsubscribe = () => {
          console.log("Unsubscribing from Supabase auth listener...");
          // Call the unsubscribe method on the subscription object
          listenerData.subscription?.unsubscribe();
          // Unsubscribe from the profile channel as well
          get()
            .profileSubscriptionChannel?.unsubscribe()
            .catch((error) =>
              console.error(
                "Error unsubscribing from profile channel during cleanup:",
                error
              )
            );
          // Clear the subscriptions from state
          set({ authSubscription: null, profileSubscriptionChannel: null });
        };

        return unsubscribe;
      }, // End of initializeAuthListener

      /**
       * Initiates sign-in using Google OAuth.
       * Redirects the user to Google's login page.
       * The result (session) is handled by the onAuthStateChange listener after redirect.
       */
      signInWithGoogle: async () => {
        set({ isLoading: true }); // Show loading while redirecting
        try {
          // --- Trigger Supabase Google OAuth flow ---
          window.trackEvent("google_signin_initiate"); // Track initiation
          const { error } = await supabase.auth.signInWithOAuth({
            provider: "google",
            options: {
              // Redirect back to the app (e.g., home page) after Google auth
              redirectTo: window.location.origin + "/home",
            },
          });
          // If there's an immediate error (e.g., misconfiguration), throw it
          if (error) throw error;
          // Successful call initiates redirect; no state change needed here,
          // listener handles the SIGNED_IN event after user returns.
        } catch (error) {
          console.error("Google Sign-In error:", error);
          set({ isLoading: false }); // Stop loading on error
          // Track failure
          window.trackEvent("google_signin_failure", {
            error:
              error instanceof AuthError ? mapAuthErrorToKey(error) : "unknown",
          });
          // Throw a mapped error for the UI to display
          if (error instanceof AuthError) {
            throw new Error(mapAuthErrorToKey(error));
          } else {
            throw new Error("auth.errors.unknown");
          }
        }
      },

      /**
       * Initiates sign-in with email and password.
       * The result (session) is handled by the onAuthStateChange listener.
       */
      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          // --- Trigger Supabase email/password login ---
          window.trackEvent("login_initiate", { method: "email" }); // Track initiation
          const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });
          // If login fails (e.g., wrong password), throw the error
          if (error) throw error;
          // Successful login triggers SIGNED_IN event handled by the listener.
          // The listener will track 'auth_signed_in'
          // window.trackEvent("login_success", { method: "email" }); // Track success (tracked by listener now)
          // No need to set state directly here.
        } catch (error) {
          console.error("Login error:", error);
          set({ isLoading: false }); // Stop loading on error
          // Track failure
          window.trackEvent("login_failure", {
            method: "email",
            error:
              error instanceof AuthError ? mapAuthErrorToKey(error) : "unknown",
          });
          // Throw a mapped error for the UI
          if (error instanceof AuthError) {
            throw new Error(mapAuthErrorToKey(error));
          } else {
            throw new Error("auth.errors.unknown");
          }
        } finally {
          // Although the listener sets isLoading to false on SIGNED_IN,
          // set it false here too in case of error, ensuring UI is responsive.
          // If successful, listener might set it back to true then false quickly.
          // Consider removing this if listener timing is reliable.
          if (get().isLoading) set({ isLoading: false });
        }
      },

      /**
       * Initiates sign-up with email and password.
       * Handles potential email confirmation requirements.
       * The result (session, if auto-confirmed) is handled by the onAuthStateChange listener.
       */
      signup: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          // --- Trigger Supabase email/password signup ---
          window.trackEvent("signup_initiate", { method: "email" }); // Track initiation
          const { error } = await supabase.auth.signUp({
            email,
            password,
            // Options like `emailRedirectTo` can be added here if needed for confirmation email links
          });
          // If signup fails (e.g., user already exists), throw the error
          if (error) throw error;
          // Signup successful (user created).
          // IMPORTANT: By default, Supabase requires email confirmation.
          // The user won't be truly 'logged in' (no session yet) until they confirm.
          // The listener will handle the SIGNED_IN event *after* confirmation.
          // We set isLoading to false here as the *signup request* is done.
          set({ isLoading: false });
          window.trackEvent("signup_success", { method: "email" }); // Track success
          // TODO: Consider adding UI feedback like "Confirmation email sent".
        } catch (error) {
          console.error("Signup error:", error);
          set({ isLoading: false }); // Stop loading on error
          // Track failure
          window.trackEvent("signup_failure", {
            method: "email",
            error:
              error instanceof AuthError ? mapAuthErrorToKey(error) : "unknown",
          });
          // Throw a mapped error for the UI
          if (error instanceof AuthError) {
            throw new Error(mapAuthErrorToKey(error));
          } else {
            throw new Error("auth.errors.unknown");
          }
        }
      },

      /**
       * Initiates sign-out.
       * The result (no session) is handled by the onAuthStateChange listener.
       */
      logout: async () => {
        set({ isLoading: true });
        try {
          // --- Trigger Supabase sign out ---
          window.trackEvent("logout_initiate"); // Track initiation
          const { error } = await supabase.auth.signOut();
          // If sign out fails for some reason, throw the error
          if (error) throw error;
          // Successful logout triggers SIGNED_OUT event handled by the listener.
          // The listener will track 'auth_signed_out'
          // window.trackEvent("logout_success"); // Track success (tracked by listener now)
          // No need to set state directly here.
        } catch (error) {
          console.error("Logout error:", error);
          set({ isLoading: false }); // Stop loading on error
          // Track failure
          window.trackEvent("logout_failure", {
            error:
              error instanceof AuthError ? mapAuthErrorToKey(error) : "unknown",
          });
          // Throw a mapped error for the UI
          if (error instanceof AuthError) {
            throw new Error(mapAuthErrorToKey(error));
          } else {
            throw new Error("auth.errors.unknown");
          }
        } finally {
          // Similar to login, ensure loading is false after the call attempt.
          if (get().isLoading) set({ isLoading: false });
        }
      },

      /**
       * Initiates sign-in using phone number OTP.
       * Sends an OTP to the provided phone number.
       */
      signInWithPhone: async (phoneNumber: string) => {
        set({
          isLoading: true,
          isOtpSent: false,
          phoneNumberForVerification: null,
        });
        try {
          window.trackEvent("login_initiate", { method: "phone" });
          const { error } = await supabase.auth.signInWithOtp({
            phone: phoneNumber,
          });
          if (error) throw error;
          // OTP sent successfully. Store the phone number for verification step.
          set({
            isLoading: false,
            isOtpSent: true,
            phoneNumberForVerification: phoneNumber,
          });
          window.trackEvent("phone_otp_sent", { phone: phoneNumber });
        } catch (error) {
          console.error("Phone Sign-In OTP error:", error);
          set({
            isLoading: false,
            isOtpSent: false,
            phoneNumberForVerification: null,
          });
          window.trackEvent("login_failure", {
            method: "phone",
            error:
              error instanceof AuthError ? mapAuthErrorToKey(error) : "unknown",
          });
          if (error instanceof AuthError) {
            throw new Error(mapAuthErrorToKey(error));
          } else {
            throw new Error("auth.errors.unknown");
          }
        }
      },

      /**
       * Verifies the phone OTP entered by the user for login.
       * The result (session) is handled by the onAuthStateChange listener.
       */
      verifyPhoneOtp: async (token: string) => {
        const phoneNumber = get().phoneNumberForVerification;
        if (!phoneNumber) {
          console.error("verifyPhoneOtp called without a phone number stored.");
          throw new Error("auth.errors.phoneVerificationMissingNumber");
        }
        set({ isLoading: true });
        try {
          window.trackEvent("phone_otp_verify_initiate");
          const { error } = await supabase.auth.verifyOtp({
            phone: phoneNumber,
            token: token,
            type: "sms", // Specify the type for login/signup
          });
          if (error) throw error;
          // Verification successful, listener will handle SIGNED_IN.
          set({ isOtpSent: false, phoneNumberForVerification: null }); // Clear temporary state
          // Listener will track 'auth_signed_in'
        } catch (error) {
          console.error("Phone OTP Verification error:", error);
          set({ isLoading: false }); // Keep isOtpSent=true maybe? Or reset? Resetting seems safer.
          window.trackEvent("phone_otp_verify_failure", {
            error:
              error instanceof AuthError ? mapAuthErrorToKey(error) : "unknown",
          });
          if (error instanceof AuthError) {
            throw new Error(mapAuthErrorToKey(error));
          } else {
            throw new Error("auth.errors.invalidOtp"); // More specific error for OTP failure
          }
        } finally {
          // Similar to login, ensure loading is false after the call attempt.
          if (get().isLoading) set({ isLoading: false });
        }
      },

      /**
       * Initiates updating the user's phone number.
       * Sends an OTP to the *new* phone number for verification.
       * Requires the user to be logged in.
       */
      updatePhoneNumber: async (newPhoneNumber: string) => {
        if (!get().isAuthenticated || !get().session) {
          console.error("updatePhoneNumber called while not authenticated.");
          throw new Error("auth.errors.notAuthenticated");
        }
        set({
          isLoading: true,
          isOtpSent: false,
          phoneNumberForVerification: null,
        });
        try {
          window.trackEvent("phone_update_initiate");
          const { error } = await supabase.auth.updateUser({
            phone: newPhoneNumber,
          });
          if (error) throw error;
          // OTP sent to new number. Store it for verification.
          set({
            isLoading: false,
            isOtpSent: true,
            phoneNumberForVerification: newPhoneNumber,
          });
          window.trackEvent("phone_update_otp_sent", {
            new_phone: newPhoneNumber,
          });
        } catch (error) {
          console.error("Update Phone Number OTP error:", error);
          set({
            isLoading: false,
            isOtpSent: false,
            phoneNumberForVerification: null,
          });
          window.trackEvent("phone_update_failure", {
            error:
              error instanceof AuthError ? mapAuthErrorToKey(error) : "unknown",
          });
          if (error instanceof AuthError) {
            throw new Error(mapAuthErrorToKey(error));
          } else {
            throw new Error("auth.errors.unknown");
          }
        }
      },

      /**
       * Verifies the OTP sent to the new phone number during an update request.
       * The result (updated user) is handled by the onAuthStateChange listener (USER_UPDATED event).
       */
      verifyPhoneUpdateOtp: async (token: string) => {
        const newPhoneNumber = get().phoneNumberForVerification;
        if (!newPhoneNumber) {
          console.error(
            "verifyPhoneUpdateOtp called without a new phone number stored."
          );
          throw new Error("auth.errors.phoneVerificationMissingNumber");
        }
        if (!get().isAuthenticated || !get().session) {
          console.error("verifyPhoneUpdateOtp called while not authenticated.");
          throw new Error("auth.errors.notAuthenticated");
        }
        set({ isLoading: true });
        try {
          window.trackEvent("phone_update_verify_initiate");
          // Use 'phone_change' type for verification during update
          const { error } = await supabase.auth.verifyOtp({
            phone: newPhoneNumber, // Use the *new* phone number stored temporarily
            token: token,
            type: "phone_change",
          });
          if (error) throw error;
          // Verification successful. Listener will handle USER_UPDATED event.
          set({ isOtpSent: false, phoneNumberForVerification: null }); // Clear temporary state
          window.trackEvent("phone_update_success");
        } catch (error) {
          console.error("Phone Update OTP Verification error:", error);
          set({ isLoading: false });
          window.trackEvent("phone_update_verify_failure", {
            error:
              error instanceof AuthError ? mapAuthErrorToKey(error) : "unknown",
          });
          if (error instanceof AuthError) {
            throw new Error(mapAuthErrorToKey(error));
          } else {
            throw new Error("auth.errors.invalidOtp"); // More specific error for OTP failure
          }
        } finally {
          if (get().isLoading) set({ isLoading: false });
        }
      },

      /**
       * Initiates anonymous sign-in.
       * Creates a temporary user session.
       * The result (session) is handled by the onAuthStateChange listener.
       */
      signInAnonymously: async () => {
        set({ isLoading: true });
        try {
          window.trackEvent("login_initiate", { method: "anonymous" });
          const { error } = await supabase.auth.signInAnonymously();
          if (error) throw error;
          // Successful anonymous sign-in triggers SIGNED_IN event handled by the listener.
          // Listener will track 'auth_signed_in'
        } catch (error) {
          console.error("Anonymous Sign-In error:", error);
          set({ isLoading: false });
          window.trackEvent("login_failure", {
            method: "anonymous",
            error:
              error instanceof AuthError ? mapAuthErrorToKey(error) : "unknown",
          });
          if (error instanceof AuthError) {
            throw new Error(mapAuthErrorToKey(error));
          } else {
            throw new Error("auth.errors.unknown");
          }
        } finally {
          // Listener sets isLoading to false on SIGNED_IN, but set false here in case of error.
          if (get().isLoading) set({ isLoading: false });
        }
      },

      // --- Anonymous User Conversion Actions ---
      linkWithGoogle: async () => {
        const currentUser = get().user;
        if (!currentUser?.is_anonymous) {
          console.warn(
            "linkWithGoogle called for non-anonymous or unauthenticated user."
          );
          // Optionally throw an error or just return if not anonymous
          // throw new Error("auth.errors.notAnonymous");
          return; // Prevent action if not anonymous
        }

        set({ isLoading: true });
        try {
          window.trackEvent?.("anon_link_initiate", { method: "google" });
          const { error } = await supabase.auth.linkIdentity({
            provider: "google",
            options: {
              redirectTo: window.location.origin + "/home", // Adjust redirect as needed
            },
          });

          if (error) {
            if (
              error.status === 409 ||
              error.message.includes("identity already exists")
            ) {
              console.warn(
                "Google account already linked to another user (409)."
              );
              window.trackEvent?.("anon_link_failure", {
                method: "google",
                error: "auth.errors.identityAlreadyExists",
              });
              throw new Error("auth.errors.identityAlreadyExists");
            }
            throw error;
          }
          // On successful initiation, Supabase redirects. State updates (isLoading=false, user.is_anonymous=false)
          // will be handled by the initializeAuthListener upon return and USER_UPDATED event.
        } catch (error) {
          console.error("Google Linking error:", error);
          set({ isLoading: false });
          const errorKey =
            error instanceof Error && error.message.startsWith("auth.errors.")
              ? error.message
              : error instanceof AuthError
              ? mapAuthErrorToKey(error)
              : "auth.errors.unknown";
          window.trackEvent?.("anon_link_failure", {
            method: "google",
            error: errorKey,
          });
          throw new Error(errorKey);
        }
      },
      linkWithEmail: async (email: string) => {
        const currentUser = get().user;
        if (!currentUser?.is_anonymous) {
          console.warn(
            "linkWithEmail called for non-anonymous or unauthenticated user."
          );
          return; // Prevent action
        }

        set({
          isLoading: true,
          isOtpSent: false,
          emailForVerification: null,
        });
        try {
          window.trackEvent?.("anon_link_initiate", { method: "email" });
          const { error } = await supabase.auth.updateUser({
            email: email,
            // options: { emailRedirectTo: ... } // Optional for magic link
          });

          if (error) {
            if (
              error.status === 409 ||
              error.message.includes("identity already exists")
            ) {
              console.warn("Email already linked to another user (409).");
              window.trackEvent?.("anon_link_failure", {
                method: "email",
                error: "auth.errors.identityAlreadyExists",
              });
              throw new Error("auth.errors.identityAlreadyExists");
            }
            throw error;
          }
          set({
            isLoading: false,
            isOtpSent: true,
            emailForVerification: email,
          });
          window.trackEvent?.("anon_link_email_sent", { email: email });
        } catch (error) {
          console.error("Email Linking Send error:", error);
          set({
            isLoading: false,
            isOtpSent: false,
            emailForVerification: null,
          });
          const errorKey =
            error instanceof Error && error.message.startsWith("auth.errors.")
              ? error.message
              : error instanceof AuthError
              ? mapAuthErrorToKey(error)
              : "auth.errors.unknown";
          window.trackEvent?.("anon_link_failure", {
            method: "email",
            error: errorKey,
          });
          throw new Error(errorKey);
        }
      },
      verifyEmailLinkOtp: async (token: string) => {
        const email = get().emailForVerification;
        if (!email) {
          console.error("verifyEmailLinkOtp called without an email stored.");
          throw new Error("auth.errors.emailVerificationMissing");
        }
        // No need to check is_anonymous here, Supabase handles the context

        set({ isLoading: true });
        try {
          window.trackEvent?.("anon_link_email_verify_initiate");
          const { error } = await supabase.auth.verifyOtp({
            email: email,
            token: token,
            type: "email_change",
          });

          if (error) {
            if (
              error.status === 409 ||
              error.message.includes("identity already exists")
            ) {
              console.warn(
                "Email verification failed, identity already exists (409)."
              );
              window.trackEvent?.("anon_link_email_verify_failure", {
                error: "auth.errors.identityAlreadyExists",
              });
              throw new Error("auth.errors.identityAlreadyExists");
            }
            throw error;
          }
          // Verification successful. Listener handles USER_UPDATED.
          set({ isOtpSent: false, emailForVerification: null }); // Clear temporary state
          window.trackEvent?.("anon_link_email_verify_success");
        } catch (error) {
          console.error("Email Link OTP/Token Verification error:", error);
          set({ isLoading: false });
          const errorKey =
            error instanceof Error && error.message.startsWith("auth.errors.")
              ? error.message
              : error instanceof AuthError
              ? mapAuthErrorToKey(error)
              : "auth.errors.invalidOtp"; // Default to invalidOtp for verification failures
          window.trackEvent?.("anon_link_email_verify_failure", {
            error: errorKey,
          });
          throw new Error(errorKey);
        } finally {
          if (get().isLoading) set({ isLoading: false });
        }
      },
      setLinkedPassword: async (password: string) => {
        const currentUser = get().user;
        if (!currentUser) {
          console.error("setLinkedPassword called while not authenticated.");
          throw new Error("auth.errors.notAuthenticated");
        }
        // Allow setting password even if user *was* anonymous but is now linked
        // if (currentUser.is_anonymous) {
        //     console.error("setLinkedPassword called for an anonymous user. Link identity first.");
        //     throw new Error("auth.errors.linkIdentityFirst");
        // }

        set({ isLoading: true });
        try {
          window.trackEvent?.("account_set_password_initiate");
          const { error } = await supabase.auth.updateUser({
            password: password,
          });

          if (error) throw error;

          set({ isLoading: false });
          window.trackEvent?.("account_set_password_success");
          // Listener might fire USER_UPDATED, but primary state change is isLoading.
        } catch (error) {
          console.error("Set Password error:", error);
          set({ isLoading: false });
          const errorKey =
            error instanceof Error && error.message.startsWith("auth.errors.")
              ? error.message
              : error instanceof AuthError
              ? mapAuthErrorToKey(error)
              : "auth.errors.unknown";
          window.trackEvent?.("account_set_password_failure", {
            error: errorKey,
          });
          throw new Error(errorKey);
        }
      },
    }), // End of store actions and state definition
    {
      // --- Persistence Configuration (zustand/middleware) ---
      name: "auth-storage", // Key used in localStorage
      storage: createJSONStorage(() => localStorage), // Use localStorage adapter
      // Specify which parts of the state to persist.
      // We persist user, session, and token to quickly restore UI on refresh.
      // `isLoading`, `isAuthenticated` are derived/managed by the listener.
      // `authSubscription` is runtime-only and shouldn't be persisted.
      partialize: (state) => ({
        user: state.user,
        session: state.session,
        token: state.token,
        // DO NOT persist phoneNumberForVerification or isOtpSent
        // Persisting isAuthenticated might cause brief inconsistency until listener runs
        // isAuthenticated: state.isAuthenticated
      }),
      // This function runs after state is loaded from localStorage
      onRehydrateStorage: () => (state) => {
        console.log("Rehydrated auth state from localStorage", state);
        // Ensure loading is true on rehydration. The listener (`initializeAuthListener`)
        // will run shortly after and determine the definitive auth state,
        // setting isLoading to false then.
        // Also ensure the persisted channel reference is cleared, as it's runtime only.
        if (state) {
          state.isLoading = true;
          state.profileSubscriptionChannel = null; // Clear persisted channel reference
          state.phoneNumberForVerification = null; // Ensure transient state is cleared
          state.isOtpSent = false; // Ensure transient state is cleared
          state.emailForVerification = null; // Ensure transient state is cleared
          state.previousAnonymousUserId = null; // Ensure cleared on rehydrate
        }
      },
    }
  ) // End of persist middleware
); // End of create store call

// --- Internal Helper for Data Transfer ---
// This function is not part of the public store state/actions
// It assumes an 'images' table with a 'user_id' column.
// Adjust table/column names if your schema differs.
const transferAnonymousUserData = async (
  oldUserId: string,
  newUserId: string
) => {
  if (!oldUserId || !newUserId || oldUserId === newUserId) {
    console.log("[Data Transfer] Skipping, invalid IDs provided.");
    return;
  }
  console.log(
    `[Data Transfer] Attempting to transfer data from anonymous user ${oldUserId} to permanent user ${newUserId}`
  );
  try {
    // *** Replace 'images' and 'user_id' if your schema uses different names ***
    const { error: updateError, count } = await supabase
      .from("images") // <= Your table name for images
      .update({ user_id: newUserId }) // <= Your user ID column name
      .eq("user_id", oldUserId); // <= Your user ID column name

    if (updateError) {
      console.error(
        "[Data Transfer] Error updating user_id for images:",
        updateError
      );
      // Optional: Add tracking for failed transfers
      window.trackEvent?.("anon_data_transfer_failure", {
        error: updateError.message,
        oldUserId,
        newUserId,
      });
      // Should we throw? Or just log? Logging might be safer.
    } else {
      console.log(
        `[Data Transfer] Successfully transferred ${
          count ?? 0
        } image records from ${oldUserId} to ${newUserId}`
      );
      if (count && count > 0) {
        window.trackEvent?.("anon_data_transfer_success", {
          count,
          oldUserId,
          newUserId,
        });
      }
    }
  } catch (error) {
    console.error(
      "[Data Transfer] Unexpected error during data transfer:",
      error
    );
    window.trackEvent?.("anon_data_transfer_failure", {
      error: error instanceof Error ? error.message : "Unknown error",
      oldUserId,
      newUserId,
    });
  }
};
